import React, { useState } from "react";
import {
    Modal,
    Upload,
    Button,
    Steps,
    Card,
    Typography,
    Table,
    Tag,
    Alert,
    Descriptions,
    Space,
    Divider,
    Progress,
    message,
    Tooltip,
    Badge,
    Collapse
} from "antd";
import {
    InboxOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileExcelOutlined,
    RocketOutlined,
    ArrowLeftOutlined,
    CodeOutlined,
    FolderAddOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { PostActivitat } from "../../../../dao/jutge/PostActivitat";
import { PostJutgeActivitatIdProblema } from "../../../../dao/jutge/PostJutgeActivitatIdProblema";
import { PostJutgeProblemaIdTest } from "../../../../dao/jutge/PostJutgeProblemaIdTest";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface Props {
    visible: boolean;
    userId: string;
    onClose: () => void;
    onSuccess: () => void;
}

export interface ParsedActGeneral {
    nom: string;
    id_aula: number;
    estat: number; // 1: borrador, 2: cerrado, 3: activo, 4: revisión
    is_examen: boolean;
    is_concurs: boolean;
    is_ide: boolean;
    inici?: string | null;
    final?: string | null;
    escena?: string | null;
    context?: string;
    javamons?: number;
    minim?: number;
}

export interface ParsedActExercici {
    codi_exercici: string; // ID temporal al excel per relacionar amb tests
    nom: string;
    descripcio: string;
    dificultat: string;
    tags: string;
    multi: boolean;
    puntuacion: number;
    inici?: string | null;
    final?: string | null;
    entrada?: string;
    sortida?: string;
}

export interface ParsedActTest {
    codi_exercici: string;
    name: string;
    tipus: "es" | "method" | "methodtest" | "content";
    entrada?: string;
    sortida?: string;
    methodName?: string;
    returnType?: string;
    modifiers?: string[];
    parameters?: Array<{ paramName: string; paramType: string }>;
    contentText?: string;
    mustContain?: boolean;
}

export const ImportarActivitatCompletaExcelModal: React.FC<Props> = ({
    visible,
    userId,
    onClose,
    onSuccess
}) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [parsedGeneral, setParsedGeneral] = useState<ParsedActGeneral | null>(null);
    const [parsedExercicis, setParsedExercicis] = useState<ParsedActExercici[]>([]);
    const [parsedTests, setParsedTests] = useState<ParsedActTest[]>([]);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitProgress, setSubmitProgress] = useState<number>(0);
    const [statusMessage, setStatusMessage] = useState<string>("");

    const resetState = () => {
        setCurrentStep(0);
        setParsedGeneral(null);
        setParsedExercicis([]);
        setParsedTests([]);
        setValidationErrors([]);
        setValidationWarnings([]);
        setIsSubmitting(false);
        setSubmitProgress(0);
        setStatusMessage("");
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetState();
            onClose();
        }
    };

    // 1. Download Template
    const handleDownloadTemplate = () => {
        try {
            const wb = XLSX.utils.book_new();

            // Full 1: Activitat
            const actData = [
                ["Camp", "Valor", "Descripcio / Opcions"],
                ["Nom", "Tema 1: Fonaments de Programació", "Nom del mòdul / activitat (Obligatori)"],
                ["ID_Aula", 1, "ID de l'aula on es vincularà (Obligatori, número)"],
                ["Estat", 3, "3: Actiu | 1: No visible / Esborrany | 2: Tancat | 4: Revisió"],
                ["Es_Examen", "NO", "SI | NO (mode examen)"],
                ["Es_Concurs", "NO", "SI | NO (mode concurs)"],
                ["Es_IDE", "NO", "SI | NO (habilita IDE web)"],
                ["Inici", "2026-10-01 08:00:00", "Data d'inici de l'activitat YYYY-MM-DD HH:mm:ss"],
                ["Final", "2026-10-31 23:59:59", "Data de tancament YYYY-MM-DD HH:mm:ss"],
                ["Escena", "", "Nom de l'escena Unity associada (opcional)"],
                ["Context", "Introducció bàsica a Java", "Descripció o context addicional (opcional)"]
            ];
            const wsAct = XLSX.utils.aoa_to_sheet(actData);
            wsAct["!cols"] = [{ wch: 18 }, { wch: 45 }, { wch: 55 }];
            XLSX.utils.book_append_sheet(wb, wsAct, "Activitat");

            // Full 2: Exercicis
            const exData = [
                [
                    "Codi_Exercici",
                    "Nom",
                    "Descripcio",
                    "Dificultat",
                    "Tags",
                    "Multi_File",
                    "Puntuacio",
                    "Inici",
                    "Final",
                    "Entrada_Exemple",
                    "Sortida_Exemple"
                ],
                [
                    "EX1",
                    "Hola Món",
                    "Imprimeix 'Hola Mon!' a la pantalla.",
                    "beginner",
                    "#intro #prints",
                    "NO",
                    1,
                    "2026-10-01 08:00:00",
                    "2026-10-31 23:59:59",
                    "",
                    "Hola Mon!\n"
                ],
                [
                    "EX2",
                    "Suma de dos enters",
                    "Llegeix dos enters i mostra la seva suma.",
                    "beginner",
                    "#e/s #operadors",
                    "NO",
                    1,
                    "2026-10-01 08:00:00",
                    "2026-10-31 23:59:59",
                    "3 5",
                    "8\n"
                ]
            ];
            const wsEx = XLSX.utils.aoa_to_sheet(exData);
            wsEx["!cols"] = [
                { wch: 15 },
                { wch: 25 },
                { wch: 45 },
                { wch: 12 },
                { wch: 18 },
                { wch: 12 },
                { wch: 12 },
                { wch: 20 },
                { wch: 20 },
                { wch: 18 },
                { wch: 18 }
            ];
            XLSX.utils.book_append_sheet(wb, wsEx, "Exercicis");

            // Full 3: Tests
            const testsData = [
                [
                    "Codi_Exercici",
                    "Nom_Test",
                    "Tipus",
                    "Entrada",
                    "Sortida",
                    "Nom_Metode",
                    "Tipo_Retorn",
                    "Modificadors",
                    "Parametres",
                    "Contingut_Codi",
                    "Ha_De_Contenir"
                ],
                [
                    "EX1",
                    "Test Salutació",
                    "es",
                    "",
                    "Hola Mon!\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    "EX2",
                    "Test Suma Positius",
                    "es",
                    "4 6\n",
                    "10\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    "EX2",
                    "Test Suma Negatius",
                    "es",
                    "-2 5\n",
                    "3\n",
                    "",
                    "",
                    "",
                    "",
                    "",
                    ""
                ],
                [
                    "EX2",
                    "Verificar Scanner",
                    "content",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "Scanner",
                    "SI"
                ]
            ];
            const wsTests = XLSX.utils.aoa_to_sheet(testsData);
            wsTests["!cols"] = [
                { wch: 15 },
                { wch: 25 },
                { wch: 12 },
                { wch: 20 },
                { wch: 20 },
                { wch: 16 },
                { wch: 14 },
                { wch: 16 },
                { wch: 16 },
                { wch: 18 },
                { wch: 14 }
            ];
            XLSX.utils.book_append_sheet(wb, wsTests, "Tests");

            // Full 4: Ajuda
            const ajudaData = [
                ["Guia per a la Càrrega Massiva d'Activitats i Exercicis"],
                [""],
                ["1. Full 'Activitat': Defineix els paràmetres globals de l'activitat/mòdul."],
                ["2. Full 'Exercicis': Llista els exercicis a crear. Fes servir 'Codi_Exercici' (ex: EX1, EX2) per identificar-los."],
                ["3. Full 'Tests': Relaciona cadascun dels tests amb el 'Codi_Exercici' corresponent."],
                ["4. Seguretat en bucle: Si la creació de qualsevol exercici o test falla, la importació s'atura per evitar dades incompletes."]
            ];
            const wsAyuda = XLSX.utils.aoa_to_sheet(ajudaData);
            XLSX.utils.book_append_sheet(wb, wsAyuda, "Instruccions");

            XLSX.writeFile(wb, "Plantilla_Activitat_Completa.xlsx");
            message.success("Plantilla d'activitat completa descarregada");
        } catch (error) {
            console.error("Error en descarregar plantilla:", error);
            message.error("Error en generar la plantilla Excel");
        }
    };

    // 2. Parse file
    const handleFileUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const buffer = e.target?.result;
                const wb = XLSX.read(buffer, { type: "array" });

                const errors: string[] = [];
                const warnings: string[] = [];

                if (!wb.SheetNames.includes("Activitat")) {
                    errors.push("Falta el full obligatori 'Activitat'. Utilitza la plantilla oficial.");
                }
                if (!wb.SheetNames.includes("Exercicis")) {
                    errors.push("Falta el full obligatori 'Exercicis'. Utilitza la plantilla oficial.");
                }

                if (errors.length > 0) {
                    setValidationErrors(errors);
                    setCurrentStep(1);
                    return;
                }

                // Parse Activitat
                const wsAct = wb.Sheets["Activitat"];
                const actRows: any[][] = XLSX.utils.sheet_to_json(wsAct, { header: 1 });
                const actMap: Record<string, any> = {};

                for (let i = 1; i < actRows.length; i++) {
                    const row = actRows[i];
                    if (row && row[0] !== undefined) {
                        const key = String(row[0]).trim().toLowerCase();
                        actMap[key] = row[1];
                    }
                }

                const nom = String(actMap["nom"] || actMap["nombre"] || "").trim();
                const id_aula = Number(actMap["id_aula"] || actMap["aula"] || 0);
                const estat = Number(actMap["estat"] || actMap["estado"] || 3);
                const is_examen = String(actMap["es_examen"] || "NO").trim().toUpperCase() === "SI";
                const is_concurs = String(actMap["es_concurs"] || "NO").trim().toUpperCase() === "SI";
                const is_ide = String(actMap["es_ide"] || "NO").trim().toUpperCase() === "SI";
                const inici = actMap["inici"] || actMap["inicio"] ? String(actMap["inici"] || actMap["inicio"]).trim() : null;
                const final = actMap["final"] || actMap["fin"] ? String(actMap["final"] || actMap["fin"]).trim() : null;
                const escena = actMap["escena"] ? String(actMap["escena"]).trim() : null;
                const context = actMap["context"] ? String(actMap["context"]).trim() : "";

                if (!nom) errors.push("El camp 'Nom' és obligatori al full 'Activitat'.");
                if (!id_aula) errors.push("El camp 'ID_Aula' és obligatori al full 'Activitat' i ha de ser un número vàlid.");

                const actGen: ParsedActGeneral = {
                    nom,
                    id_aula,
                    estat,
                    is_examen,
                    is_concurs,
                    is_ide,
                    inici,
                    final,
                    escena,
                    context
                };

                // Parse Exercicis
                const wsEx = wb.Sheets["Exercicis"];
                const exRows: any[][] = XLSX.utils.sheet_to_json(wsEx, { header: 1 });
                const exercicis: ParsedActExercici[] = [];
                const codisSet = new Set<string>();

                for (let i = 1; i < exRows.length; i++) {
                    const row = exRows[i];
                    if (!row || row.length === 0 || !row[0]) continue;

                    const codi = String(row[0]).trim().toUpperCase();
                    const exNom = String(row[1] || "").trim();
                    const exDesc = String(row[2] || "").trim();
                    const exDif = String(row[3] || "beginner").trim().toLowerCase();
                    const exTags = String(row[4] || "#exercici").trim();
                    const exMulti = String(row[5] || "NO").trim().toUpperCase() === "SI";
                    const exPunts = Number(row[6]) > 0 ? Number(row[6]) : 1;
                    const exInici = row[7] ? String(row[7]).trim() : null;
                    const exFinal = row[8] ? String(row[8]).trim() : null;
                    const exEntrada = row[9] !== undefined ? String(row[9]) : "";
                    const exSortida = row[10] !== undefined ? String(row[10]) : "";

                    if (!exNom) {
                        errors.push(`Fila d'exercici ${i} (Codi ${codi}): no té nom definit.`);
                    }

                    codisSet.add(codi);
                    exercicis.push({
                        codi_exercici: codi,
                        nom: exNom,
                        descripcio: exDesc,
                        dificultat: ["beginner", "intermediate", "expert"].includes(exDif) ? exDif : "beginner",
                        tags: exTags,
                        multi: exMulti,
                        puntuacion: exPunts,
                        inici: exInici,
                        final: exFinal,
                        entrada: exEntrada,
                        sortida: exSortida
                    });
                }

                if (exercicis.length === 0) {
                    errors.push("No s'ha trobat cap exercici al full 'Exercicis'.");
                }

                // Parse Tests (optional sheet)
                const tests: ParsedActTest[] = [];
                if (wb.SheetNames.includes("Tests")) {
                    const wsTests = wb.Sheets["Tests"];
                    const testRows: any[][] = XLSX.utils.sheet_to_json(wsTests, { header: 1 });

                    for (let i = 1; i < testRows.length; i++) {
                        const row = testRows[i];
                        if (!row || row.length === 0 || !row[0]) continue;

                        const codi = String(row[0]).trim().toUpperCase();
                        const tNom = String(row[1] || `Test ${i}`).trim();
                        const tTipus = String(row[2] || "es").trim().toLowerCase() as any;
                        const tEntrada = row[3] !== undefined ? String(row[3]) : "";
                        const tSortida = row[4] !== undefined ? String(row[4]) : "";
                        const tMetode = row[5] !== undefined ? String(row[5]).trim() : "";
                        const tRetorn = row[6] !== undefined ? String(row[6]).trim() : "void";
                        const tModRaw = row[7] !== undefined ? String(row[7]) : "";
                        const tParamRaw = row[8] !== undefined ? String(row[8]) : "";
                        const tContent = row[9] !== undefined ? String(row[9]) : "";
                        const tMust = String(row[10] || "SI").trim().toUpperCase() === "SI";

                        if (!codisSet.has(codi)) {
                            warnings.push(`Test '${tNom}' fa referència al codi '${codi}', que no existeix al full Exercicis.`);
                        }

                        const modifiers = tModRaw ? tModRaw.split(",").map(s => s.trim()).filter(Boolean) : ["public", "static"];
                        const parameters: Array<{ paramName: string; paramType: string }> = [];
                        if (tParamRaw) {
                            for (const p of tParamRaw.split(",")) {
                                const tr = p.trim();
                                if (tr) {
                                    const tokens = tr.split(/\s+/);
                                    if (tokens.length >= 2) {
                                        parameters.push({ paramType: tokens[0], paramName: tokens[1] });
                                    } else {
                                        parameters.push({ paramType: tokens[0], paramName: `p${parameters.length + 1}` });
                                    }
                                }
                            }
                        }

                        tests.push({
                            codi_exercici: codi,
                            name: tNom,
                            tipus: ["es", "method", "methodtest", "content"].includes(tTipus) ? tTipus : "es",
                            entrada: tEntrada,
                            sortida: tSortida,
                            methodName: tMetode,
                            returnType: tRetorn,
                            modifiers,
                            parameters,
                            contentText: tContent,
                            mustContain: tMust
                        });
                    }
                }

                setParsedGeneral(actGen);
                setParsedExercicis(exercicis);
                setParsedTests(tests);
                setValidationErrors(errors);
                setValidationWarnings(warnings);
                setCurrentStep(1);
            } catch (err: any) {
                console.error("Error processant fitxer:", err);
                message.error("Error al processar el fitxer Excel d'activitat.");
            }
        };
        reader.readAsArrayBuffer(file);
    };

    // 3. Confirm and loop execution
    const handleConfirmImport = async () => {
        if (!parsedGeneral || parsedExercicis.length === 0) {
            message.error("Dades insuficients per realitzar la importació");
            return;
        }

        setIsSubmitting(true);
        setSubmitProgress(5);
        setStatusMessage("Creant activitat / mòdul base...");

        try {
            // 1. Create Activity
            const actPayload: any = {
                id: 0,
                nom: parsedGeneral.nom,
                estat: parsedGeneral.estat,
                id_aula: parsedGeneral.id_aula,
                is_examen: parsedGeneral.is_examen ? 1 : 0,
                is_concurs: parsedGeneral.is_concurs ? 1 : 0,
                is_ide: parsedGeneral.is_ide ? 1 : 0,
                inici: parsedGeneral.inici || null,
                fin: parsedGeneral.final || null,
                escena: parsedGeneral.escena || null,
                context: parsedGeneral.context || "",
                id_usuari: userId,
                tipus: 0,
                javamons: 10,
                minim: 0,
                inst: "",
                ras: []
            };

            const createdActRes: any = await PostActivitat(actPayload);
            const actId = createdActRes?.id;

            if (!actId) {
                throw new Error("No s'ha pogut obtenir l'ID de l'activitat creada.");
            }

            setSubmitProgress(20);
            setStatusMessage(`Activitat creada (ID #${actId}). Important ${parsedExercicis.length} exercicis en bucle...`);

            const totalItems = parsedExercicis.length;
            let completedExCount = 0;

            // 2. Loop through Exercises
            for (const ex of parsedExercicis) {
                const exPayload: any = {
                    id: 0,
                    nom: ex.nom,
                    descripcio: ex.descripcio,
                    dificultat: ex.dificultat,
                    tags: ex.tags,
                    multi: ex.multi ? 1 : 0,
                    puntuacion: ex.puntuacion,
                    entrada: ex.entrada || "",
                    sortida: ex.sortida || "",
                    inici: ex.inici || null,
                    fin: ex.final || null
                };

                const createdExRes: any = await PostJutgeActivitatIdProblema(String(actId), exPayload);
                let createdExId: number | null = null;
                if (createdExRes && typeof createdExRes.id === "number") {
                    createdExId = createdExRes.id;
                } else if (createdExRes && typeof createdExRes.id === "string") {
                    createdExId = parseInt(createdExRes.id, 10);
                } else if (typeof createdExRes === "number") {
                    createdExId = createdExRes;
                }

                if (!createdExId) {
                    throw new Error(`Fallada en crear l'exercici '${ex.nom}'. S'ha cancel·lat la importació en bucle.`);
                }

                // 3. Tests for this exercise
                const associatedTests = parsedTests.filter(t => t.codi_exercici === ex.codi_exercici);
                for (const t of associatedTests) {
                    let testPayload: any = {
                        name: t.name,
                        mode: t.tipus,
                        tipus: t.tipus
                    };

                    if (t.tipus === "es") {
                        testPayload = {
                            ...testPayload,
                            entrada: t.entrada || "",
                            sortida: t.sortida || ""
                        };
                    } else if (t.tipus === "methodtest") {
                        testPayload = {
                            ...testPayload,
                            entrada: t.entrada || "",
                            sortida: t.sortida || "",
                            methodName: t.methodName || "metode"
                        };
                    } else if (t.tipus === "method") {
                        testPayload = {
                            ...testPayload,
                            methodName: t.methodName || "metode",
                            modifiers: t.modifiers || ["public", "static"],
                            returnType: t.returnType || "void",
                            parameters: t.parameters || [],
                            fitxer: JSON.stringify({
                                methodName: t.methodName || "metode",
                                modifiers: t.modifiers || ["public", "static"],
                                returnType: t.returnType || "void",
                                parameters: t.parameters || []
                            })
                        };
                    } else if (t.tipus === "content") {
                        testPayload = {
                            ...testPayload,
                            searchText: t.contentText || "",
                            mustContain: t.mustContain !== undefined ? t.mustContain : true,
                            fitxer: JSON.stringify({
                                text: t.contentText || "",
                                must: t.mustContain !== undefined ? t.mustContain : true
                            })
                        };
                    }

                    try {
                        await PostJutgeProblemaIdTest(String(createdExId), testPayload);
                    } catch (tErr: any) {
                        throw new Error(`Error creant el test '${t.name}' per l'exercici '${ex.nom}': ${tErr?.message || tErr}`);
                    }
                }

                completedExCount++;
                const exProgress = 20 + Math.round((completedExCount / totalItems) * 75);
                setSubmitProgress(exProgress);
                setStatusMessage(`Exercici ${completedExCount} de ${totalItems} creat (${ex.nom})...`);
            }

            setSubmitProgress(100);
            setStatusMessage("¡Activitat completa i tots els exercicis/tests creats amb èxit!");
            message.success("¡Activitat i exercicis importats amb èxit!");

            setTimeout(() => {
                resetState();
                onSuccess();
                onClose();
            }, 600);
        } catch (error: any) {
            console.error("Error important activitat completa:", error);
            message.error(`Error durant la càrrega massiva: ${error?.message || "Error desconegut"}`);
            setIsSubmitting(false);
        }
    };

    const exColumns = [
        { title: "Codi", dataIndex: "codi_exercici", key: "codi", width: 80 },
        { title: "Nom", dataIndex: "nom", key: "nom", width: 200 },
        {
            title: "Dificultat",
            dataIndex: "dificultat",
            key: "dif",
            width: 100,
            render: (d: string) => <Tag color={d === "beginner" ? "green" : d === "intermediate" ? "orange" : "red"}>{d}</Tag>
        },
        {
            title: "Tests associats",
            key: "tests",
            render: (_: any, r: ParsedActExercici) => {
                const count = parsedTests.filter(t => t.codi_exercici === r.codi_exercici).length;
                return <Badge count={count} style={{ backgroundColor: count > 0 ? "#52c41a" : "#faad14" }} />;
            }
        }
    ];

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            width={900}
            title={
                <Space>
                    <FolderAddOutlined style={{ color: "#217346", fontSize: 20 }} />
                    <span>Càrrega Massiva d'Activitats i Exercicis des d'Excel</span>
                </Space>
            }
            footer={null}
            destroyOnClose
        >
            <div style={{ padding: "8px 0" }}>
                <Steps
                    current={currentStep}
                    size="small"
                    style={{ marginBottom: 20 }}
                    items={[
                        { title: "Pujar / Descarregar Excel" },
                        { title: "Validació i Previsualització" },
                        { title: "Importació en Bucle" }
                    ]}
                />

                {currentStep === 0 && (
                    <div>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Importació d'activitats senceres amb exercicis i tests"
                            description="Pots descarregar la plantilla massiva, especificar els paràmetres de l'activitat, la llista d'exercicis i els seus casos de prova. Tot s'inserirà automàticament en bucle segur."
                        />

                        <div style={{ textAlign: "center", marginBottom: 20 }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadTemplate}
                                type="dashed"
                                size="large"
                                style={{ borderColor: "#217346", color: "#217346" }}
                            >
                                Descarregar Plantilla Activitat Completa (.xlsx)
                            </Button>
                        </div>

                        <Dragger
                            accept=".xlsx, .xls"
                            showUploadList={false}
                            beforeUpload={(file) => {
                                handleFileUpload(file);
                                return false;
                            }}
                            style={{ padding: "30px 10px" }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#217346", fontSize: 48 }} />
                            </p>
                            <p className="ant-upload-text">Arrossega el fitxer Excel omplert aquí, o fes clic per seleccionar-lo</p>
                            <p className="ant-upload-hint">Format suportat: .xlsx generat amb la plantilla de l'activitat completa</p>
                        </Dragger>
                    </div>
                )}

                {currentStep === 1 && (
                    <div>
                        {validationErrors.length > 0 && (
                            <Alert
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="Errors que impedeixen la importació:"
                                description={
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {validationErrors.map((err, i) => (
                                            <li key={i}>{err}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        {validationWarnings.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="Avisos detectats:"
                                description={
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {validationWarnings.map((warn, i) => (
                                            <li key={i}>{warn}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        )}

                        {parsedGeneral && (
                            <Card size="small" title="Metadades de l'Activitat" style={{ marginBottom: 16 }}>
                                <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                                    <Descriptions.Item label="Nom">{parsedGeneral.nom}</Descriptions.Item>
                                    <Descriptions.Item label="Aula ID">{parsedGeneral.id_aula}</Descriptions.Item>
                                    <Descriptions.Item label="Estat">
                                        <Tag color={parsedGeneral.estat === 3 ? "green" : "orange"}>
                                            {parsedGeneral.estat === 3 ? "ACTIU" : "BORRADOR"}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Examen / Concurs / IDE">
                                        {parsedGeneral.is_examen ? <Tag color="blue">Examen</Tag> : null}
                                        {parsedGeneral.is_concurs ? <Tag color="gold">Concurs</Tag> : null}
                                        {parsedGeneral.is_ide ? <Tag color="purple">IDE Web</Tag> : null}
                                        {!parsedGeneral.is_examen && !parsedGeneral.is_concurs && !parsedGeneral.is_ide ? <Tag>Normal</Tag> : null}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Dates">
                                        {parsedGeneral.inici || parsedGeneral.final ? (
                                            <span>{parsedGeneral.inici || "Sense inici"} ➔ {parsedGeneral.final || "Sense final"}</span>
                                        ) : "Sense restricció"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Escena">{parsedGeneral.escena || "Cap"}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        )}

                        <Card size="small" title={`Exercicis a Crear (${parsedExercicis.length})`} style={{ marginBottom: 16 }}>
                            <Table
                                dataSource={parsedExercicis}
                                columns={exColumns}
                                rowKey="codi_exercici"
                                size="small"
                                pagination={{ pageSize: 5 }}
                            />
                        </Card>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                            <Button icon={<ArrowLeftOutlined />} onClick={() => setCurrentStep(0)} disabled={isSubmitting}>
                                Tornar
                            </Button>
                            <Button
                                type="primary"
                                icon={<RocketOutlined />}
                                disabled={validationErrors.length > 0}
                                loading={isSubmitting}
                                onClick={handleConfirmImport}
                                style={{ backgroundColor: "#217346", borderColor: "#217346" }}
                            >
                                Confirmar i Crear Activitat Completa
                            </Button>
                        </div>
                    </div>
                )}

                {isSubmitting && (
                    <div style={{ textAlign: "center", padding: "30px 20px" }}>
                        <Progress percent={submitProgress} status="active" strokeColor="#217346" />
                        <p style={{ marginTop: 16, fontWeight: 600, color: "#217346" }}>{statusMessage}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ImportarActivitatCompletaExcelModal;
